import type { ThemeConfig } from 'antd';
import {
  navy,
  green,
  blue,
  gray,
  semantic,
  shadows,
  overlay,
  focusRing,
  typography,
  layout,
} from './tokens';

/**
 * Tema do Ant Design v6 na identidade Palmer.
 * Mapa completo em §7 de Docs/design/design-system-palmer.md.
 *
 * Decisão que atravessa tudo: o PRIMÁRIO é navy, não verde. Branco sobre
 * `#70b746` dá 2.46:1 e reprova WCAG AA até para texto grande; o verde é
 * assinatura geométrica (barra de acento, ink bar), não superfície de texto.
 */
export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: navy[800],
    colorPrimaryHover: navy[700],
    colorPrimaryActive: navy[900],
    colorPrimaryBg: navy[100],
    colorPrimaryBgHover: navy[200],
    colorPrimaryBorder: navy[300],

    colorSuccess: semantic.success,
    colorSuccessBg: semantic.successBg,
    colorSuccessBorder: semantic.successBorder,

    colorWarning: semantic.warning,
    colorWarningBg: semantic.warningBg,
    colorWarningBorder: semantic.warningBorder,

    colorError: semantic.error,
    colorErrorBg: semantic.errorBg,
    colorErrorBorder: semantic.errorBorder,

    colorInfo: semantic.info,
    colorInfoBg: semantic.infoBg,
    colorInfoBorder: semantic.infoBorder,

    colorLink: blue[600],
    colorLinkHover: blue[700],
    colorLinkActive: blue[800],

    colorTextBase: gray[900],
    colorText: gray[900],
    colorTextSecondary: gray[600],
    colorTextTertiary: gray[500],
    colorTextQuaternary: gray[400],

    colorBgBase: '#ffffff',
    colorBgLayout: gray[100],
    colorBgContainer: '#ffffff',
    colorBgElevated: '#ffffff',
    colorBgSpotlight: gray[900],
    colorBgMask: overlay,

    colorBorder: gray[200],
    colorBorderSecondary: gray[100],

    colorFill: 'rgba(33, 38, 49, 0.08)',
    colorFillSecondary: 'rgba(33, 38, 49, 0.05)',
    colorFillTertiary: 'rgba(33, 38, 49, 0.03)',
    colorFillQuaternary: 'rgba(33, 38, 49, 0.02)',

    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,

    fontFamily: typography.fontFamily,
    fontSize: 14,
    fontSizeHeading1: 24,
    fontSizeHeading2: 20,
    fontSizeHeading3: 16,
    fontSizeHeading4: 14,
    fontWeightStrong: 700,

    controlHeight: 40,
    controlHeightSM: 32,
    controlHeightLG: 48,

    motionDurationFast: '0.12s',
    motionDurationMid: '0.2s',
    motionDurationSlow: '0.3s',

    boxShadow: shadows.md,
    boxShadowSecondary: shadows.lg,
  },
  components: {
    Layout: {
      headerBg: '#ffffff',
      headerHeight: layout.headerHeight,
      headerPadding: '0 24px',
      siderBg: navy[800], // sidebar navy, assinatura da marca
      bodyBg: gray[100],
      triggerBg: navy[900],
      triggerColor: '#ffffff',
    },
    Menu: {
      // sidebar usa theme="dark"
      darkItemBg: navy[800],
      darkSubMenuItemBg: '#1c2f5b',
      darkItemColor: navy[300], // 6.63:1
      darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
      darkItemHoverColor: '#ffffff',
      darkItemSelectedBg: navy[900],
      darkItemSelectedColor: '#ffffff',
      darkGroupTitleColor: navy[400],
      itemBorderRadius: 6,
      itemMarginInline: 8,
      itemHeight: 40,
      // menus claros (dropdowns de contexto)
      itemSelectedBg: navy[100],
      itemSelectedColor: navy[800],
      itemHoverColor: navy[800],
    },
    Button: {
      controlHeight: 40,
      borderRadius: 6,
      fontWeight: 600,
      primaryShadow: 'none', // sem sombra colorida, institucional
      dangerShadow: 'none',
      defaultBorderColor: gray[200],
      defaultColor: gray[900],
      defaultHoverBorderColor: navy[800],
      defaultHoverColor: navy[800],
    },
    Input: {
      controlHeight: 40,
      borderRadius: 6,
      paddingInline: 12,
      activeBorderColor: navy[800],
      hoverBorderColor: navy[700],
      activeShadow: focusRing, // halo navy; o azul-AntD morre
      colorTextPlaceholder: gray[400],
    },
    InputNumber: {
      controlHeight: 40,
      borderRadius: 6,
      activeBorderColor: navy[800],
      hoverBorderColor: navy[700],
      activeShadow: focusRing,
    },
    Select: {
      controlHeight: 40,
      borderRadius: 6,
      optionSelectedBg: navy[100],
      optionSelectedColor: navy[800],
      optionActiveBg: navy[50],
      activeBorderColor: navy[800],
      hoverBorderColor: navy[700],
      activeOutlineColor: 'rgba(33, 54, 104, 0.12)',
    },
    DatePicker: {
      controlHeight: 40,
      borderRadius: 6,
      activeBorderColor: navy[800],
      hoverBorderColor: navy[700],
      activeShadow: focusRing,
      cellActiveWithRangeBg: blue[100],
      colorPrimary: navy[800],
    },
    Card: {
      borderRadiusLG: 8,
      headerFontSize: 16,
      headerHeight: 56,
      paddingLG: 24,
      colorBorderSecondary: gray[100],
      boxShadowTertiary: shadows.sm,
    },
    Table: {
      headerBg: gray[50],
      headerColor: gray[700],
      headerSplitColor: 'transparent', // sem divisores verticais no header
      borderColor: gray[100],
      rowHoverBg: navy[50],
      rowSelectedBg: navy[100],
      rowSelectedHoverBg: navy[200],
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      headerBorderRadius: 8,
      footerBg: gray[50],
    },
    Tabs: {
      inkBarColor: green[500], // verde da marca, o traço da assinatura
      itemColor: gray[600],
      itemSelectedColor: navy[800],
      itemHoverColor: navy[800],
      titleFontSize: 14,
      horizontalItemPadding: '12px 4px',
      horizontalItemGutter: 24,
    },
    Tag: {
      borderRadiusSM: 4,
      defaultBg: gray[100],
      defaultColor: gray[700],
    },
    Modal: {
      borderRadiusLG: 8,
      titleFontSize: 16,
      titleColor: gray[900],
      // Padding do modal vive no GlobalStyles (`.ant-modal .ant-modal-body`):
      // o AntD v6 não expõe token de padding para Modal.
      boxShadow: shadows.lg,
    },
    Drawer: {
      paddingLG: 24,
      colorBgElevated: '#ffffff',
    },
    Steps: {
      // `colorPrimary` já cobre ícone ativo e concluído; o v6 não tem token
      // separado para a borda do passo concluído.
      colorPrimary: navy[800],
      navArrowColor: gray[300],
      colorTextDescription: gray[500],
    },
    Descriptions: {
      labelBg: gray[50],
      titleColor: gray[900],
      colorTextSecondary: gray[600],
      itemPaddingBottom: 12,
    },
    Alert: {
      borderRadiusLG: 8,
      colorInfoBg: semantic.infoBg,
      colorInfoBorder: semantic.infoBorder,
      colorSuccessBg: semantic.successBg,
      colorSuccessBorder: semantic.successBorder,
      colorWarningBg: semantic.warningBg,
      colorWarningBorder: semantic.warningBorder,
      colorErrorBg: semantic.errorBg,
      colorErrorBorder: semantic.errorBorder,
    },
    Message: {
      contentPadding: '10px 16px',
      borderRadiusLG: 8,
      boxShadow: shadows.md,
    },
    Badge: {
      colorError: semantic.error,
      indicatorHeight: 20,
      textFontSize: 12,
    },
    Segmented: {
      itemSelectedBg: '#ffffff',
      itemSelectedColor: navy[800],
      trackBg: gray[100],
      itemColor: gray[600],
      borderRadius: 6,
      trackPadding: 3,
    },
    Form: {
      labelColor: gray[700],
      labelFontSize: 14,
      labelRequiredMarkColor: semantic.error,
      itemMarginBottom: 20,
      verticalLabelPadding: '0 0 6px',
    },
  },
};
